import React, { useMemo, type ReactNode } from 'react';
import { useFragment } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ViewPagePersonalizationReader } from '@confluence/content-unified-query/entry-points/ViewPagePersonalizationReader.ts';
import { buildViewPagePersonalizationVariables } from '@confluence/content-unified-query/entry-points/viewPagePersonalizationVariables.ts';

import { CatchupPersonalizationContext } from './hooks/CatchupPersonalizationContext.ts';
import { useDynamicSplitButtonCatchupLastViewedTimeFragmentReader } from './hooks/useDynamicSplitButton.ts';
import type { useDynamicSplitButton_catchupLastViewedTimeReader$key } from './hooks/__generated__/useDynamicSplitButton_catchupLastViewedTimeReader.graphql.ts';

type Props = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	isLivePage?: boolean;
	isInObjectSideBar?: boolean;
	children: ReactNode;
};

const CatchupFragmentReader = ({
	contentId,
	contentType,
	fragmentRef,
	children,
}: Props & {
	fragmentRef: useDynamicSplitButton_catchupLastViewedTimeReader$key | null;
}): React.JSX.Element => {
	const data = useFragment(useDynamicSplitButtonCatchupLastViewedTimeFragmentReader, fragmentRef);
	const value = useMemo(
		() => ({
			contentId,
			contentType: contentType === 'blogpost' ? ('BLOGPOST' as const) : ('PAGE' as const),
			data: data ?? null,
		}),
		[contentId, contentType, data],
	);
	return (
		<CatchupPersonalizationContext.Provider value={value}>
			{children}
		</CatchupPersonalizationContext.Provider>
	);
};

export const CatchupPersonalization = (props: Props): React.JSX.Element => {
	const { cloudId, isAdminHubAIEnabled } = useSessionData();
	if (
		isAdminHubAIEnabled &&
		!props.isLivePage &&
		!props.isInObjectSideBar &&
		(props.contentType === 'page' || props.contentType === 'blogpost') &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_catchup_reader') &&
		expVal('confluence_exp_personalization_contract', 'isEnabled', false)
	) {
		return (
			<ViewPagePersonalizationReader
				variables={buildViewPagePersonalizationVariables(
					{
						cloudId,
						contentId: props.contentId,
						spaceKey: props.spaceKey,
						isBlog: props.contentType === 'blogpost',
					},
					{ catchup: true },
				)}
				fallback={props.children}
			>
				{(data) =>
					data.catchupGetLastViewedTime === undefined ? (
						props.children
					) : (
						<CatchupFragmentReader {...props} fragmentRef={data.catchupGetLastViewedTime} />
					)
				}
			</ViewPagePersonalizationReader>
		);
	}
	return <>{props.children}</>;
};
